import { col, makeTargetOutput, NETWORK_DIRECT, POTENTIAL_RATES, type Shape } from './base.ts';

export const DAILY_ROLLUP: Shape = [
  // Allocated production — volume + rate per phase.
  col('dayAllocProdAllocatedOil', 'liquidVolume', 'Daily allocated oil production volume.'),
  col('dayAllocProdAllocatedOilRate', 'liquidRate', 'Daily allocated oil production rate.'),
  col('dayAllocProdAllocatedGas', 'gasVolume', 'Daily allocated gas production volume.'),
  col('dayAllocProdAllocatedGasRate', 'gasRate', 'Daily allocated gas production rate.'),
  col('dayAllocProdAllocatedWater', 'liquidVolume', 'Daily allocated water production volume.'),
  col('dayAllocProdAllocatedWaterRate', 'liquidRate', 'Daily allocated water production rate.'),
  // Allocated injection — volume + rate per phase.
  col('dayAllocInjGasInjection', 'gasVolume', 'Daily allocated gas injection volume.'),
  col('dayAllocInjGasInjectionRate', 'gasRate', 'Daily allocated gas injection rate.'),
  col('dayAllocInjWaterInjection', 'liquidVolume', 'Daily allocated water injection volume.'),
  col('dayAllocInjWaterInjectionRate', 'liquidRate', 'Daily allocated water injection rate.'),
  // Unconstrained potential rates.
  ...POTENTIAL_RATES,
  // Planning target rates. Source spelling `targetOilproduction` (lowercase p).
  col('targetOilproduction', 'liquidVolume', 'Target oil production from planning.'),
  col('targetGasProduction', 'gasVolumeModel', 'Target gas production from planning.'),
  col('targetWaterProduction', 'liquidVolume', 'Target water production from planning.'),
  col('targetGasInjection', 'gasVolumeModel', 'Target gas injection from planning.'),
  col('targetWaterInjection', 'liquidVolume', 'Target water injection from planning.'),
];
/** Daily allocated field production + injection, de-prefixed to clean phase
 * canonicals — volume + rate per phase for production (oil/gas/water) and
 * injection (gas/water). The `AllocatedOutput` daily sub-view's measure family,
 * carved out of {@link DAILY_ROLLUP}'s `dayAllocProd*`/`dayAllocInj*` columns. */
export const ALLOCATED_OUTPUT: Shape = [
  col('oil', 'liquidVolume', 'Daily allocated oil production volume.', { suffix: 'dayAllocProdAllocatedOil' }),
  col('oilRate', 'liquidRate', 'Daily allocated oil production rate.', { suffix: 'dayAllocProdAllocatedOilRate' }),
  col('gas', 'gasVolume', 'Daily allocated gas production volume.', { suffix: 'dayAllocProdAllocatedGas' }),
  col('gasRate', 'gasRate', 'Daily allocated gas production rate.', { suffix: 'dayAllocProdAllocatedGasRate' }),
  col('water', 'liquidVolume', 'Daily allocated water production volume.', { suffix: 'dayAllocProdAllocatedWater' }),
  col('waterRate', 'liquidRate', 'Daily allocated water production rate.', {
    suffix: 'dayAllocProdAllocatedWaterRate',
  }),
  col('gasInjection', 'gasVolume', 'Daily allocated gas injection volume.', { suffix: 'dayAllocInjGasInjection' }),
  col('gasInjectionRate', 'gasRate', 'Daily allocated gas injection rate.', { suffix: 'dayAllocInjGasInjectionRate' }),
  col('waterInjection', 'liquidVolume', 'Daily allocated water injection volume.', {
    suffix: 'dayAllocInjWaterInjection',
  }),
  col('waterInjectionRate', 'liquidRate', 'Daily allocated water injection rate.', {
    suffix: 'dayAllocInjWaterInjectionRate',
  }),
];

/** Planning-target daily field volumes (FIELD grain) for the `TargetOutput` daily
 * sub-view. Uses the `DailyRollup*_Agent` source spelling `targetOilproduction`
 * (lowercase `p`). */
export const TARGET_OUTPUT_DAILY: Shape = makeTargetOutput('targetOilproduction');

/**
 * The pipeline/flowline-grain relation carried by the NetworkRisk pipeline
 * views: the direct-relation to the Pipeline model entity. The source
 * already names it `pipelineEntity`, so it is kept verbatim as the grain ref
 * (passthrough, like `IDENTITY`/`STRING_RELATION`) — the Agent source carries
 * no prefix, so `prefix:''` yields `source === canonical` and no rename.
 */
export const PIPELINE_RELATION: Shape = [
  col('pipelineEntity', 'unitless', 'Pipeline/flowline entity this row belongs to.'),
];

/**
 * The entity-grain relation carried by `NetworkRisk_OptimizationScenarios`
 * The source names it `entityName` (a direct-relation to the generic
 * Entity registry — well, field, string, or facility — despite the name), so it
 * is kept verbatim as the grain ref. Distinct from the daily-rollup
 * `WELL_RELATION` (which fronts `DailyRollupOutput_Well`'s well-typed
 * `entityName`): here the entity may be any iPSO entity type.
 */
export const NETWORK_RISK_ENTITY_RELATION: Shape = [
  col('entityName', 'unitless', 'Reference to the entity (well, field, string, or facility) this risk row belongs to.'),
];

/**
 * The parent-network relation present on every NetworkRisk source: a
 * constant direct-relation to the network model entity. The source names it
 * `network` (NOT `networkName` like `NETWORK_RELATION`), so it is a distinct
 * single-column passthrough — `suffix === canonical`, `prefix:''`.
 */
export const NETWORK_RISK_RELATION: Shape = [NETWORK_DIRECT];

/**
 * Per-scenario run metadata carried only by the NetworkRisk
 * `*_OptimizationScenarios` sources: the scenario label and the run
 * identifier. `scenarioName` is a scalar string; `runID` is a TimeSeries at the
 * source (not a scalar id — preserved verbatim, do NOT retype). Both are bare
 * source columns — `suffix === canonical`, `prefix:''` — kept alongside identity
 * so the agent can scope by / label the scenario.
 */
